import {
  MouseEvent,
  MouseEventHandler,
  ReactNode,
  useCallback,
  useLayoutEffect,
  useState,
} from 'react';
import { DialogProps } from '@mui/material';

import { DialogTitle, DialogTitleColor } from './types';

interface IUseDialogProps {
  initialTitle: string;
  onClose?: () => void;
  onManualClose?: () => void;
  shouldStopPropagationOnClose?: boolean;
  title?: ReactNode;
}

export const useDialog = ({
  initialTitle,
  onClose,
  onManualClose,
  shouldStopPropagationOnClose,
  title,
}: IUseDialogProps) => {
  const [dialogTitle, setDialogTitle] = useState<DialogTitle>({
    title: initialTitle,
    color: DialogTitleColor.Regular,
  });

  const handleClose = useCallback<Required<DialogProps>['onClose']>(
    (event, reason) => {
      if (shouldStopPropagationOnClose) {
        (event as MouseEvent<HTMLButtonElement>).stopPropagation();
      }

      onClose?.();
      const isBackdropClick = reason === 'backdropClick';
      const isEscapeKeyDown = reason === 'escapeKeyDown';

      if (isBackdropClick || isEscapeKeyDown) {
        onManualClose?.();
      }
    },
    [onClose, onManualClose, shouldStopPropagationOnClose],
  );

  const onCloseIconClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    event => {
      if (shouldStopPropagationOnClose) {
        event.stopPropagation();
      }

      onClose?.();
      onManualClose?.();
    },
    [onClose, onManualClose, shouldStopPropagationOnClose],
  );

  useLayoutEffect(() => {
    setDialogTitle({ title });
  }, [title]);

  // disable propagation to prevent routing on event bubbling
  const handleEventBubbling = useCallback(
    (event: MouseEvent) => event.stopPropagation(),
    [],
  );

  return {
    dialogTitle,
    handleClose,
    onCloseIconClick,
    handleEventBubbling,
  };
};
